import type { Appearance, CssFontSource } from '@stripe/stripe-js'
import type { ResolvedTheme } from '@/hooks/use-resolved-theme'

/**
 * Stripe Elements render inside an iframe that cannot read this document's CSS
 * custom properties, so the design tokens have to be duplicated as literals.
 * Keep these values in sync with `src/styles.css` by hand - `--chart-*` is
 * deliberately absent because Stripe's Appearance API cannot parse oklch().
 */
const CHECKOUT_TOKENS: Record<ResolvedTheme, Record<string, string>> = {
  light: {
    // The payment panel sits on `bg-card`, not `bg-background`.
    card: '#fffefb',
    foreground: '#16151a',
    mutedForeground: '#5c5a54',
    primary: '#e6b340',
    destructive: '#d92d20',
    border: '#ddd8cb',
    accent: '#efece4',
  },
  dark: {
    card: '#101013',
    foreground: '#f4f4f5',
    mutedForeground: '#a1a1aa',
    primary: '#e6b340',
    destructive: '#f87171',
    border: '#26262c',
    accent: '#17171b',
  },
}

// --radius is 0.5rem at a 16px root.
const CHECKOUT_BORDER_RADIUS = '8px'
const CHECKOUT_FONT_FAMILY =
  "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"

/**
 * DM Sans, loaded into the Elements iframe from the same Google Fonts
 * stylesheet as `index.html`.
 */
export const CHECKOUT_FONTS: CssFontSource[] = [
  {
    cssSrc:
      'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,100..1000;1,9..40,100..1000&display=swap',
  },
]

/** Appearance for the custom checkout Elements in the given resolved theme. */
export function buildCheckoutAppearance(mode: ResolvedTheme): Appearance {
  const token = CHECKOUT_TOKENS[mode]

  return {
    variables: {
      colorPrimary: token.primary,
      colorBackground: token.card,
      colorText: token.foreground,
      colorTextSecondary: token.mutedForeground,
      colorDanger: token.destructive,
      borderRadius: CHECKOUT_BORDER_RADIUS,
      fontFamily: CHECKOUT_FONT_FAMILY,
      spacingUnit: '4px',
    },
    rules: {
      '.Input': {
        backgroundColor: token.card,
        border: `1px solid ${token.border}`,
        boxShadow: 'none',
        color: token.foreground,
      },
      '.Input:focus': {
        border: `1px solid ${token.primary}`,
        boxShadow: `0 0 0 3px ${token.primary}33`,
        outline: 'none',
      },
      '.Input--invalid': {
        border: `1px solid ${token.destructive}`,
        boxShadow: 'none',
        color: token.destructive,
      },
      '.Label': {
        color: token.mutedForeground,
        fontWeight: '500',
      },
      '.Tab': {
        backgroundColor: token.accent,
        border: `1px solid ${token.border}`,
        boxShadow: 'none',
        color: token.foreground,
      },
      '.Tab--selected': {
        backgroundColor: token.card,
        border: `1px solid ${token.primary}`,
        boxShadow: 'none',
        color: token.foreground,
      },
      '.Error': {
        color: token.destructive,
      },
    },
  }
}
